<template>
  <div class="app-surface">
    <NavBar />

    <div class="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
      <!-- 加载状态 -->
      <div v-if="isLoading" class="flex justify-center py-24">
        <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-400"></div>
      </div>

      <!-- 错误状态 -->
      <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-2xl p-8 text-center">
        <div class="text-red-400 text-4xl mb-4">⚠️</div>
        <h3 class="text-lg font-medium text-red-700 mb-2">加载失败</h3>
        <p class="text-red-500 text-sm mb-5">{{ error }}</p>
        <router-link to="/books" class="px-5 py-2 bg-red-500 text-white font-medium rounded-xl hover:bg-red-600">
          返回书库
        </router-link>
      </div>

      <!-- 书籍详情 -->
      <div v-else-if="book" class="space-y-6">
        <!-- 基本信息 -->
        <div class="overflow-hidden rounded-2xl border border-jackfruit-100 bg-white shadow-soft">
          <div class="grid gap-6 p-5 md:grid-cols-[220px_1fr] md:p-8">
            <div class="mx-auto w-44 overflow-hidden rounded-xl shadow-fruit md:w-full">
              <img
                v-if="book.coverUrl"
                :src="book.coverUrl"
                :alt="book.title"
                class="aspect-[3/4] w-full object-cover"
                @error="(e) => ((e.target as HTMLImageElement).style.display = 'none')"
              />
              <div v-else class="grid aspect-[3/4] place-items-center bg-jackfruit-50 text-5xl">📖</div>
            </div>

            <div>
              <div class="mb-4 flex items-start justify-between gap-4">
                <div>
                  <div class="mb-2 inline-flex rounded-lg bg-jackfruit-100 px-3 py-1 text-xs font-black text-ink-700">
                    {{ categoryLabel }} · {{ book.totalChapters }}章
                  </div>
                  <h1 class="text-3xl font-black text-ink-900 md:text-4xl">{{ book.title }}</h1>
                  <p class="mt-2 text-sm font-medium text-ink-500">{{ book.author }}</p>
                </div>
                <span class="hidden rounded-lg bg-berry-50 px-3 py-1.5 text-xs font-black text-berry-600 sm:inline-flex">
                  🔥 {{ formatViewCount(book.viewCount) }}追读
                </span>
              </div>

              <p class="mb-6 max-w-3xl text-sm leading-7 text-ink-600">{{ book.description }}</p>

              <div class="mb-6 grid grid-cols-3 gap-3 rounded-xl bg-jackfruit-50 p-3 text-center">
                <div>
                  <p class="text-lg font-black text-ink-900">{{ formatViewCount(book.viewCount) }}</p>
                  <p class="text-xs text-ink-500">追读</p>
                </div>
                <div>
                  <p class="text-lg font-black text-ink-900">{{ book.totalChapters }}</p>
                  <p class="text-xs text-ink-500">章节</p>
                </div>
                <div>
                  <p class="text-lg font-black text-ink-900">{{ book.isAiGenerated ? 'AI' : '精选' }}</p>
                  <p class="text-xs text-ink-500">来源</p>
                </div>
              </div>

              <div class="flex flex-wrap gap-3">
                <button
                  @click="startReading"
                  class="rounded-xl bg-jackfruit-500 px-6 py-3 text-sm font-black text-ink-900 shadow-fruit transition hover:bg-jackfruit-600"
                >
                  开始阅读
                </button>
                <button
                  v-if="!isInBookshelf"
                  @click="addToBookshelf"
                  :disabled="isAddingToBookshelf"
                  class="rounded-xl border border-jackfruit-200 bg-white px-6 py-3 text-sm font-black text-ink-700 transition hover:bg-jackfruit-50 disabled:opacity-50"
                >
                  <span v-if="isAddingToBookshelf" class="animate-spin mr-2">⟳</span>
                  加入书架
                </button>
                <div v-else class="rounded-xl bg-leaf-50 px-6 py-3 text-sm font-black text-leaf-600">
                  已在书架
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="rounded-2xl border border-ink-100 bg-white p-5 shadow-soft md:p-6">
          <h2 class="mb-5 text-lg font-black text-ink-900">章节列表</h2>
          <div v-if="chapters.length === 0" class="py-8 text-center text-sm text-ink-500">
            暂无章节
          </div>
          <div v-else class="space-y-1">
            <router-link
              v-for="ch in chapters"
              :key="ch.id"
              :to="`/reading/${book.id}/${ch.chapterNumber}`"
              class="group flex items-center justify-between rounded-xl px-4 py-3 transition-colors hover:bg-jackfruit-50"
              @click.prevent="openChapter(ch.chapterNumber)"
            >
              <div class="flex items-center gap-3 min-w-0">
                <span class="flex-shrink-0 text-sm font-bold text-ink-300">第{{ ch.chapterNumber }}章</span>
                <span class="truncate text-ink-600 transition-colors group-hover:text-pulp-600">{{ ch.title }}</span>
              </div>
              <div class="flex shrink-0 items-center gap-2">
                <span
                  class="rounded-lg px-2 py-1 text-[11px] font-black"
                  :class="ch.chapterNumber <= 3 ? 'bg-leaf-50 text-leaf-600' : 'bg-jackfruit-100 text-ink-700'"
                >
                  {{ ch.chapterNumber <= 3 ? '免费' : '5蜜豆/3章' }}
                </span>
                <span class="text-sm text-ink-300">→</span>
              </div>
            </router-link>
          </div>
        </div>
      </div>

      <div v-if="showExistingForkDialog && existingShelfBook" class="fixed inset-0 z-[60] grid place-items-center bg-ink-900/35 px-4 backdrop-blur-sm" @click.self="showExistingForkDialog = false">
        <div class="w-full max-w-sm rounded-2xl border border-jackfruit-100 bg-white p-5 shadow-fruit">
          <h2 class="text-lg font-black text-ink-900">书架中已有此书</h2>
          <p class="mt-2 text-sm leading-6 text-ink-500">
            你的书架里已经有《{{ existingShelfBook.title }}》。可以回到书架里的版本继续读，也可以把公共书当成新的阅读重新开始。
          </p>
          <div class="mt-5 grid gap-2">
            <button class="rounded-xl bg-jackfruit-500 px-4 py-3 text-sm font-black text-ink-900 shadow-fruit" @click="continueShelfReading">
              去书架继续阅读
            </button>
            <button class="rounded-xl border border-jackfruit-200 bg-white px-4 py-3 text-sm font-black text-ink-700" @click="startFreshReading">
              开启新阅读
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { bookApi, userApi } from '@/api'
import NavBar from '@/components/NavBar.vue'
import type { Book, Chapter } from '@/types/user'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

const isLoading = ref(true)
const error = ref('')
const book = ref<Book | null>(null)
const chapters = ref<Chapter[]>([])
const isInBookshelf = ref(false)
const isAddingToBookshelf = ref(false)
const pendingChapterNumber = ref(1)
const shelfBook = ref<Book | null>(null)
const existingForks = ref<Book[]>([])
const latestFork = computed(() => existingForks.value[0] || null)
const existingShelfBook = computed(() => latestFork.value || shelfBook.value)
const showExistingForkDialog = ref(false)

const categoryLabel = computed(() => {
  const map: Record<string, string> = { male: '男频', female: '女频', general: '综合', cyberpunk: '赛博朋克' }
  return map[book.value?.category || ''] || '综合'
})

const formatViewCount = (count: number) => {
  if (!count) return '0'
  if (count >= 10000) return (count / 10000).toFixed(1) + '万'
  return count.toString()
}

onMounted(async () => {
  const bookId = parseInt(route.params.id as string)
  if (!bookId) {
    error.value = '无效的书籍 ID'
    isLoading.value = false
    return
  }

  try {
    const [bookRes, chaptersRes] = await Promise.all([
      bookApi.getBook(bookId),
      bookApi.getBookChapters(bookId, { limit: 100 }),
    ])

    if (bookRes.code === 200 && bookRes.data?.book) {
      book.value = bookRes.data.book
    } else {
      throw new Error(bookRes.message || '加载书籍信息失败')
    }

    if (chaptersRes.code === 200 && chaptersRes.data?.items) {
      chapters.value = chaptersRes.data.items
    }

    // 检查是否已加入书架
    if (authStore.isAuthenticated) {
      try {
        const shelfRes = await userApi.getBookshelf()
        if (shelfRes.code === 200 && shelfRes.data?.items) {
          isInBookshelf.value = shelfRes.data.items.some(
            (b: { id: number }) => b.id === bookId
          )
          shelfBook.value = shelfRes.data.items.find((b: Book) => b.id === bookId) || null
          existingForks.value = shelfRes.data.items
            .filter((b: Book) => b.sourceBookId === bookId && b.visibility === 'private')
            .sort((a: Book, b: Book) => {
              const aTime = new Date(a.updatedAt || a.createdAt).getTime()
              const bTime = new Date(b.updatedAt || b.createdAt).getTime()
              return bTime - aTime
            })
        }
      } catch {
        // 忽略书架检查失败
      }
    }
  } catch (err: any) {
    error.value = err?.message || '加载失败，请重试'
  } finally {
    isLoading.value = false
  }
})

const addToBookshelf = async () => {
  if (!authStore.isAuthenticated) return
  isAddingToBookshelf.value = true
  try {
    await userApi.addToBookshelf(book.value!.id)
    isInBookshelf.value = true
  } catch {
    // 忽略
  } finally {
    isAddingToBookshelf.value = false
  }
}

const startReading = () => {
  openChapter(1)
}

const openChapter = (chapterNumber: number) => {
  pendingChapterNumber.value = chapterNumber
  if (book.value?.visibility === 'public' && existingShelfBook.value && route.query.new !== '1') {
    showExistingForkDialog.value = true
    return
  }
  router.push(`/reading/${book.value!.id}/${chapterNumber}`)
}

const continueShelfReading = () => {
  const target = existingShelfBook.value
  if (!target) return
  showExistingForkDialog.value = false
  router.push(`/reading/${target.id}/${Math.max(1, target.lastChapterNumber || 1)}?from=shelf`)
}

const startFreshReading = () => {
  showExistingForkDialog.value = false
  router.push(`/reading/${book.value!.id}/${pendingChapterNumber.value}?new=1`)
}
</script>
